import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../../components/ui/Layout";
import Alert from '@mui/material/Alert';
import { useCart } from "../../context/CartContext";
import { motion, AnimatePresence } from "framer-motion";
import { FiShoppingCart, FiCheckCircle, FiArrowLeft, FiStar, FiShare2, FiTruck, FiPackage, FiShield } from "react-icons/fi";

export default function ProductDetail() {
  const { productId: requestedProductId } = useParams();
  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState("");
  const [activeIndex, setActiveIndex] = useState(null);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showStockEmptyAlert, setShowStockEmptyAlert] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);

  const { cart, updateCart } = useCart();

  const faqs = [
    {
      question: "Quali sono le dimensioni del prodotto?",
      answer: product ? `Dimensioni: ${product.width}x${product.height}x${product.depth}cm` : "",
      icon: <FiPackage />
    },
    {
      question: "Quando riceverò il prodotto?",
      answer: "Il prodotto verrà spedito entro 2-3 giorni lavorativi dalla ricezione dell'ordine. Verrai contattato via email per confermare la spedizione.",
      icon: <FiTruck />
    },
    {
      question: "Offrite una garanzia sul prodotto?",
      answer: "Offriamo una garanzia di 12 mesi su tutti i nostri prodotti.",
      icon: <FiShield />
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  function addToCart() {
    const productInCart = cart.find(item => item.productId === product.productId);
  
    // Check available stock versus requested quantity
    const currentCartQuantity = productInCart?.quantity || 0;
    if (currentCartQuantity + quantity > product.quantityAvailable) {
      setShowStockEmptyAlert(true);
      setTimeout(() => {
        setShowStockEmptyAlert(false);
      }, 3000);
      return;
    }
  
    setIsAddingToCart(true);
  
    let updatedCart;
    if (productInCart) {
      // Increment quantity if item is already in the cart
      updatedCart = cart.map((item) =>
        item.productId === product.productId
          ? { ...item, quantity: item.quantity + quantity }
          : item
      );
    } else {
      // Add the product to the cart if it's not there
      updatedCart = [...cart, { ...product, quantity }];
    }
  
    updateCart(updatedCart);
  
    setTimeout(() => {
      setIsAddingToCart(false);
      setShowSuccessAlert(true);
  
      setTimeout(() => {
        setShowSuccessAlert(false);
      }, 3000);
    }, 600);
  }  

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const fetchProduct = async () => {
      try {
        const res = await fetch(
          process.env.REACT_APP_BACKEND_URI +
            "products/" +
            requestedProductId
        );
        const data = await res.json();
        setProduct(data);
        setSelectedImage(data.imageUrl);
      } catch (error) {
        console.error("Error fetching product:", error);
      }
    };

    fetchProduct();
  }, [requestedProductId]);

  // Reset quantity when product changes
  useEffect(() => {
    setQuantity(1);
  }, [product]);

  const handleQuantityChange = (amount) => {
    const newValue = Math.max(1, Math.min(quantity + amount, product.quantityAvailable));
    setQuantity(newValue);
  };

  // Loading state
  if (!product) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <motion.div 
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-16 h-16 border-4 border-gray-200 border-t-[#406580] rounded-full"
        />
      </div>
    );
  }

  return (
    <section className="relative min-h-screen bg-gray-50 pb-16">
      <Layout>
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16"
        >
          {/* Breadcrumb */}
          <nav className="flex mb-8">
            <ol className="flex items-center space-x-2">
              <li>
                <Link to="/" className="text-gray-500 hover:text-gray-700">Home</Link>
              </li>
              <li className="text-gray-500">/</li>
              <li>
                <Link to="/store" className="text-gray-500 hover:text-gray-700">Prodotti</Link>
              </li>
              <li className="text-gray-500">/</li>
              <li className="text-gray-900 font-medium truncate">{product.productName}</li>
            </ol>
          </nav>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
            {/* Left Column - Product Images */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Main Image */}
              <div className="relative aspect-square overflow-hidden bg-gray-100 rounded-xl mb-4 border border-gray-200">
                {imageLoading && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-8 h-8 border-2 border-[#406580] border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
                <motion.img
                  key={selectedImage}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: imageLoading ? 0 : 1 }}
                  transition={{ duration: 0.3 }}
                  src={selectedImage.startsWith("http") 
                    ? selectedImage 
                    : `https://res.cloudinary.com/dndm9hex2/image/upload/v1733331162/${selectedImage}`
                  }
                  alt={product.productName}
                  className="w-full h-full object-contain"
                  onLoad={() => setImageLoading(false)}
                />
              </div>

              {/* Thumbnails */}
              <div className="grid grid-cols-5 gap-2">
                {product.productImagesUrls.map((img, index) => (
                  <motion.div 
                    key={index}
                    whileHover={{ y: -5 }}
                    whileTap={{ scale: 0.95 }}
                    className={`aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer ${
                      selectedImage === img ? 'ring-2 ring-[#406580]' : ''
                    }`}
                    onClick={() => {
                      setImageLoading(true);
                      setSelectedImage(img);
                    }}
                  >
                    <img
                      src={img.startsWith("http") 
                        ? img 
                        : `https://res.cloudinary.com/dndm9hex2/image/upload/v1733331162/${img}`
                      }
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right Column - Product Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex flex-col"
            >
              {/* Category */}
              <div className="mb-2">
                <span className="inline-block px-3 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                  {product.productCategory.categoryName}
                </span>
              </div>

              {/* Product Name */}
              <h1 className="text-3xl font-bold text-gray-900 inria-serif-regular mb-2">
                {product.productName}
              </h1>

              {/* Price and Availability */}
              <div className="flex flex-wrap items-center gap-4 mt-2 mb-6">
                <span className="text-2xl font-bold text-[#406580]">
                  €{parseFloat(product.productPrice).toFixed(2)}
                </span>

                {/* Stock status */}
                {product.quantityAvailable === 0 ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Non disponibile
                  </span>
                ) : product.quantityAvailable < 10 ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    Solo {product.quantityAvailable} {product.quantityAvailable === 1 ? "pezzo" : "pezzi"} rimast{product.quantityAvailable === 1 ? "o" : "i"}
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Disponibile
                  </span>
                )}
              </div>

              {/* Description */}
              <div className="prose prose-sm max-w-none mb-8 text-gray-600">
                <p>{product.productDescription}</p>
              </div>

              {/* Attributes */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="flex items-center">
                  <div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-500 mr-3">
                    <FiPackage />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Dimensioni</p>
                    <p className="font-medium">{product.width}x{product.height}x{product.depth}cm</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="h-10 w-10 rounded-full bg-green-50 flex items-center justify-center text-green-500 mr-3">
                    <FiShield />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Garanzia</p>
                    <p className="font-medium">12 mesi</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="h-10 w-10 rounded-full bg-purple-50 flex items-center justify-center text-purple-500 mr-3">
                    <FiTruck />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Spedizione</p>
                    <p className="font-medium">2-3 giorni lavorativi</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="h-10 w-10 rounded-full bg-amber-50 flex items-center justify-center text-amber-500 mr-3">
                    <FiStar />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">SKU</p>
                    <p className="font-medium">{product.sku.toString().padStart(5, '0')}</p>
                  </div>
                </div>
              </div>

              {/* Quantity Selector */}
              <div className="flex items-center space-x-4 mb-6">
                <span className="text-gray-700 font-medium">Quantità:</span>
                <div className="flex items-center border border-gray-300 rounded-lg">
                  <motion.button
                    whileHover={{ backgroundColor: '#f3f4f6' }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleQuantityChange(-1)}
                    disabled={quantity <= 1}
                    className="px-3 py-2 text-gray-500 focus:outline-none disabled:opacity-50"
                  >
                    -
                  </motion.button>
                  <span className="w-10 text-center font-medium text-gray-700">{quantity}</span>
                  <motion.button
                    whileHover={{ backgroundColor: '#f3f4f6' }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleQuantityChange(1)}
                    disabled={quantity >= product.quantityAvailable}
                    className="px-3 py-2 text-gray-500 focus:outline-none disabled:opacity-50"
                  >
                    +
                  </motion.button>
                </div>
              </div>

              {/* Add to Cart and Wishlist Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <motion.button
                  whileHover={{ scale: 1.02, backgroundColor: '#305570' }}
                  whileTap={{ scale: 0.98 }}
                  disabled={
                    isAddingToCart ||
                    product.quantityAvailable === 0 ||
                    quantity > product.quantityAvailable
                  }
                  onClick={addToCart}
                  className="flex-1 py-3 px-8 bg-[#406580] text-white rounded-lg font-medium flex items-center justify-center space-x-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isAddingToCart ? (
                    <>
                      <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin"></div>
                      <span>Aggiunta in corso...</span>
                    </>
                  ) : (
                    <>
                      <FiShoppingCart className="mr-2" />
                      <span>Aggiungi al Carrello</span>
                    </>
                  )}
                </motion.button>
              </div>

              {/* Tags */}
              {product.labels && product.labels.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {product.labels.map((label, index) => (
                      <span 
                        key={index} 
                        className="inline-block px-3 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full"
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Share */}
              <div className="flex items-center pt-4 border-t border-gray-200">
                <span className="text-sm font-medium text-gray-900 mr-3">Condividi:</span>
                <div className="flex space-x-2">
                  <motion.button
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.9 }}
                    className="p-2 bg-gray-100 rounded-full text-gray-500 hover:text-gray-700 relative"
                    onClick={() => {
                      // Copy current URL to clipboard
                      navigator.clipboard.writeText(window.location.href)
                        .then(() => {
                          // Show a temporary tooltip
                          const tooltip = document.getElementById("share-tooltip");
                          tooltip.classList.remove("opacity-0");
                          tooltip.classList.add("opacity-100");
                          
                          setTimeout(() => {
                            tooltip.classList.remove("opacity-100");
                            tooltip.classList.add("opacity-0");
                          }, 2000);
                        })
                        .catch(err => {
                          console.error('Failed to copy: ', err);
                        });
                    }}
                  >
                    <FiShare2 size={16} />
                    <span 
                      id="share-tooltip" 
                      className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs py-1 px-2 rounded opacity-0 pointer-events-none transition-opacity duration-100"
                    >
                      Link copiato!
                    </span>
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>

          {/* FAQs Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-16"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-8 inria-serif-regular">Informazioni Aggiuntive</h2>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y divide-gray-200">
              {faqs.map((faq, index) => (
                <div key={index} className="overflow-hidden">
                  <button
                    onClick={() => toggleAccordion(index)}
                    className="flex items-center justify-between w-full px-6 py-5 text-left focus:outline-none"
                  >
                    <div className="flex items-center">
                      <div className="flex-shrink-0 mr-4 text-[#406580]">
                        {faq.icon}
                      </div>
                      <span className="font-medium text-gray-900">{faq.question}</span>
                    </div>
                    <motion.span
                      animate={{ rotate: activeIndex === index ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="flex items-center ml-6 text-gray-400"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </motion.span>
                  </button>
                  <AnimatePresence>
                    {activeIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-6 pb-5 text-gray-600"
                      >
                        <div className="pl-8">
                          {faq.answer}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </motion.div>
          
          {/* Suggested Products Section */}
          {/* This would be where you'd add related products */}
        </motion.div>
      </Layout>

      {/* Floating Alerts */}
      <AnimatePresence>
        {showSuccessAlert && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-20 right-4 z-50 max-w-sm"
          >
            <div className="bg-green-50 border-l-4 border-green-500 p-4 flex rounded-lg shadow-lg">
              <div className="flex-shrink-0">
                <FiCheckCircle className="h-5 w-5 text-green-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  Prodotto aggiunto al carrello!
                </p>
              </div>
              <button 
                className="ml-auto pl-3 flex-shrink-0 text-green-500 hover:text-green-800"
                onClick={() => setShowSuccessAlert(false)}
              >
                <span className="sr-only">Close</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </motion.div>
        )}
        
        {showStockEmptyAlert && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-20 right-4 z-50 max-w-sm"
          >
            <div className="bg-red-50 border-l-4 border-red-500 p-4 flex rounded-lg shadow-lg">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">
                  Quantità non disponibile in magazzino.
                </p>
              </div>
              <button 
                className="ml-auto pl-3 flex-shrink-0 text-red-500 hover:text-red-800"
                onClick={() => setShowStockEmptyAlert(false)}
              >
                <span className="sr-only">Close</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}